import { map } from 'lodash';
import React from 'react';
import styled from 'styled-components';

import { BaseDebugContainer } from 'src/presentation/components/pages/integrations/lookupAdapterDebugging/components/LookupDebuggingStyles';
import lookupDebuggerAutolinkingResource from 'src/actions/integrations/lookupAdapterDebugging/lookupDebuggerAutolinkingResource';
import RequestContainer from 'src/presentation/components/pages/integrations/lookupAdapterDebugging/components/RequestContainer';
import ResponseContainer, {
  StyledErrorIcon,
  RowText,
} from 'src/presentation/components/pages/integrations/lookupAdapterDebugging/components/ResponseContainer';
import { Row } from 'src/presentation/components/forms/Layout';
import { useResourceValue } from 'src/presentation/hooks/useDocumentResource';

const AutolinkingDebuggingResults = (): JSX.Element => {
  const { value: debugResults, loadingState, errors } = useResourceValue(lookupDebuggerAutolinkingResource);

  const basicResults = debugResults?.basicCollector;
  const basicRawResponse = basicResults?.response?.rawResponse && atob(basicResults.response.rawResponse);

  // eslint-disable-next-line
  const basicErrors = basicResults?.validationErrors
    ? [...basicResults?.validationErrors, ...debugResults.validationErrors]
    : debugResults.validationErrors;

  const detailedResults = debugResults?.detailedCollector;
  const detailedRawResponse = detailedResults?.response?.rawResponse && atob(detailedResults.response.rawResponse);

  const errorComponents = map(errors, (error, idx) => {
    let errorText;
    if (error.code === 'not_exist') {
      errorText = (
        <RowText data-aid="autolinking-customer-not-found-error">
          Customer not found in Gladly. Please try again with a different email or phone number.
        </RowText>
      );
    } else if (error.code === 'timeout') {
      errorText = <RowText>The request to the lookup adapter timed out.</RowText>;
    } else {
      errorText = <RowText data-aid="autolinking-general-error">Something went wrong. Please try again.</RowText>;
    }

    return (
      <Row key={`debug-error-${idx}`}>
        <StyledErrorIcon />
        {errorText}
      </Row>
    );
  });

  if (loadingState === 'unstarted') {
    return <></>;
  }

  return (
    <>
      <StepContainer>Gladly is now testing the Autolinking workflow</StepContainer>
      <StepDivider />
      {loadingState === 'succeeded' ? (
        <>
          <RequestContainer
            data-aid="autolinking-results-request-container"
            request={basicResults?.request}
            requestType="Autolinking"
          />
          <ResponseContainer
            data-aid="autolinking-results-response-container"
            requestType="Autolinking"
            response={basicResults.response}
            rawResponse={basicRawResponse}
            validationErrors={basicErrors}
          />
          {debugResults.validationErrors?.length === 0 && !basicResults?.response?.adapterError ? (
            <>
              <StepContainer>Gladly is now testing the Detailed workflow</StepContainer>
              <StepDivider />
              <RequestContainer request={detailedResults?.request} requestType="Detailed" />
              <ResponseContainer
                requestType="Detailed"
                data-aid="autolinking-results-detailed-response-container"
                response={detailedResults.response}
                rawResponse={detailedRawResponse}
                validationErrors={detailedResults?.validationErrors}
              />
            </>
          ) : null}
        </>
      ) : null}
      {loadingState === 'failed' ? <DebugStepContainer>{errorComponents}</DebugStepContainer> : null}
    </>
  );
};

export default AutolinkingDebuggingResults;

const DebugStepContainer = styled.div`
  ${BaseDebugContainer}
  margin: 12px 0 12px 24px;
`;

const StepDivider = styled.div`
  height: 16px;
  border-left: 4px dotted #767676;
  margin: 8px 24px;
`;

const StepContainer = styled.div`
  padding 0 24px;
`;
